import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { ResponsiveContainer, LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from 'recharts';

// Pickup & Pace — booking-curve analysis for Price Intelligence's Revenue suite.
// Reuses backend/priceIntelligence/pickupPace.js's pace/pickup aggregation
// (same tables as the Demand Drivers "Pace"/"Pickup" tiles, extended into full
// curves). Every "vs Last Year" figure here is an approximate MVP: last year's
// counts use CURRENT reservation status, not a historical snapshot, and the
// chart's Forecast line is an index-method extrapolation, not a statistical
// model — both caveats are surfaced in the UI, not hidden.

const fmtPct = (n) => (n == null ? '—' : `${n > 0 ? '+' : ''}${n}%`);
const fmtInt = (n) => (n == null ? '—' : Number(n).toLocaleString('en-IN'));
const fmtInr = (n) => (n == null ? '—' : `₹${Number(n).toLocaleString('en-IN')}`);
const deltaColor = (n) => (n == null ? 'var(--theme-text-muted, #64748b)' : n >= 0 ? '#059669' : '#dc2626');
const offsetLabel = (o) => (o === 0 ? 'Today' : String(o));

const sel = { padding: '6px 10px', borderRadius: 6, border: '1px solid var(--theme-border, #e5e7eb)', background: 'var(--theme-surface, #fff)', color: 'var(--theme-text, #374151)', fontSize: 13 };
const btn = (kind) => ({
  padding: '7px 13px', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer',
  border: kind === 'primary' ? 'none' : '1px solid var(--theme-border, #e5e7eb)',
  background: kind === 'primary' ? 'var(--theme-primary, #0d8f83)' : 'var(--theme-surface, #fff)',
  color: kind === 'primary' ? '#fff' : 'var(--theme-text, #374151)',
});
const card = { background: 'var(--theme-surface, #fff)', border: '1px solid var(--theme-border, #e5e7eb)', borderRadius: 12, padding: 16 };
const kpiBox = { flex: '1 1 200px', minWidth: 200, background: 'var(--theme-surface, #fff)', border: '1px solid var(--theme-border, #e5e7eb)', borderRadius: 12, padding: '12px 14px' };
const th = { textAlign: 'left', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#94a3b8', padding: '8px 10px', borderBottom: '1px solid var(--theme-border, #e5e7eb)' };
const td = { padding: '8px 10px', borderBottom: '1px solid var(--theme-border-light, #f1f5f9)', fontSize: 13 };
const emptyBox = { padding: 24, textAlign: 'center', color: 'var(--theme-text-muted, #64748b)', border: '1px dashed var(--theme-border, #e5e7eb)', borderRadius: 8 };
const banner = (kind) => ({
  padding: '9px 12px', borderRadius: 8, fontSize: 13, margin: '10px 0',
  background: kind === 'danger' ? '#fef2f2' : '#fffbeb', border: `1px solid ${kind === 'danger' ? '#fecaca' : '#fde68a'}`,
  color: kind === 'danger' ? '#b91c1c' : '#92400e',
});

function PickupPace({ token, selectedProperty }) {
  const [tab, setTab] = useState('curve');
  const [rooms, setRooms] = useState([]);
  const [room, setRoom] = useState('');
  const [days, setDays] = useState(30);
  const [curve, setCurve] = useState(null);
  const [byWeek, setByWeek] = useState([]);
  const [byStayDate, setByStayDate] = useState([]);
  const [segments, setSegments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const api = useMemo(() => async (path) => {
    const res = await fetch(`/api/v1/price-intelligence${path}`, {
      headers: {
        Authorization: `Bearer ${token}`, 'Content-Type': 'application/json',
        'x-chain-id': selectedProperty?.chain_id || '', 'x-property-id': selectedProperty?.id || '',
      },
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || `Request failed (${res.status})`);
    return data;
  }, [token, selectedProperty]);

  useEffect(() => {
    if (!selectedProperty?.id) return;
    (async () => {
      try {
        const r = await api('/own-rooms');
        setRooms(r.rooms || []);
        if (r.rooms?.length) setRoom((prev) => prev || String(r.rooms[0].own_room_id));
      } catch (e) { setError(e.message); }
    })();
  }, [selectedProperty, api]);

  const q = useMemo(() => {
    const p = new URLSearchParams({ days: String(days) });
    if (room) p.set('room', room);
    return p.toString();
  }, [days, room]);

  const load = useCallback(async () => {
    if (!selectedProperty?.id) return;
    setLoading(true); setError('');
    try {
      if (tab === 'curve') {
        setCurve(await api(`/pickup-pace/curve?${q}`));
      } else {
        const r = await api(`/pickup-pace/by-stay-date?${q}`);
        setByWeek(r.by_week || []); setByStayDate(r.by_stay_date || []);
        if (tab === 'segment') setSegments((await api(`/pickup-pace/by-segment?${q}`)).segments || []);
      }
    } catch (e) { setError(e.message); } finally { setLoading(false); }
  }, [api, q, tab, selectedProperty]);

  useEffect(() => { load(); }, [load]);

  const chartData = useMemo(() => {
    if (!curve) return [];
    const map = new Map();
    const put = (arr, key) => { for (const p of arr || []) map.set(p.offset, { ...(map.get(p.offset) || { offset: p.offset }), [key]: p.cumulative }); };
    put(curve.this_year, 'this_year'); put(curve.last_year, 'last_year'); put(curve.forecast, 'forecast');
    return [...map.values()].sort((a, b) => a.offset - b.offset);
  }, [curve]);

  if (!selectedProperty?.id) {
    return <div style={{ padding: 24, color: 'var(--theme-text-muted, #64748b)' }}>Select a property to view booking pace.</div>;
  }

  const roomName = rooms.find((r) => String(r.own_room_id) === String(room))?.name || 'room';

  return (
    <div style={{ padding: 20 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 20 }}>Pickup & Pace</h2>
          <div style={{ color: 'var(--theme-text-muted, #64748b)', fontSize: 13 }}>Track booking pace against last year and forecast.</div>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <select value={room} onChange={(e) => setRoom(e.target.value)} style={sel} title="Room type">
            {rooms.map((r) => <option key={r.own_room_id} value={r.own_room_id}>{r.name}</option>)}
          </select>
          <select value={days} onChange={(e) => setDays(Number(e.target.value))} style={sel} title="Date range">
            {[14, 30, 60, 90].map((d) => <option key={d} value={d}>Next {d} days</option>)}
          </select>
          <span style={{ ...sel, cursor: 'default', color: 'var(--theme-text-muted, #64748b)' }}>Compare: Last Year</span>
          <button onClick={load} style={btn('ghost')}>↻</button>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 6, margin: '14px 0', borderBottom: '1px solid var(--theme-border, #e5e7eb)', paddingBottom: 8 }}>
        {[['curve', 'Pickup Chart'], ['segment', 'Pickup by Segment'], ['stayDate', 'Stay Date Pace']].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} style={btn(tab === k ? 'primary' : 'ghost')} aria-pressed={tab === k}>{l}</button>
        ))}
      </div>

      {error && <div style={banner('danger')}>{error} <span onClick={() => setError('')} style={{ cursor: 'pointer', float: 'right' }}>✕</span></div>}
      {loading && <div style={{ padding: 24, textAlign: 'center', color: 'var(--theme-text-muted, #64748b)' }}>Loading…</div>}

      {!loading && tab === 'curve' && curve && (
        <div>
          <div style={{ fontSize: 12, color: 'var(--theme-text-muted, #64748b)', margin: '4px 0 12px' }}>
            {roomName} — cumulative rooms booked by day, this year vs last year at the same lead time. The dashed green tail is a short-term projection (last year's remaining pickup pattern, scaled to this year's pace), not a statistical forecast.
          </div>
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
            <div style={{ ...card, flex: '2 1 480px', minWidth: 320 }}>
              <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 8 }}>Cumulative Rooms Booked</div>
              <div style={{ width: '100%', height: 300 }}>
                <ResponsiveContainer>
                  <LineChart data={chartData} margin={{ top: 5, right: 20, bottom: 5, left: 4 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--theme-border, #e5e7eb)" />
                    <XAxis dataKey="offset" tickFormatter={offsetLabel} tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} width={36} />
                    <Tooltip labelFormatter={offsetLabel} />
                    <Legend />
                    <Line type="monotone" dataKey="this_year" name="This Year" stroke="#2563eb" strokeWidth={2} dot={{ r: 2 }} connectNulls />
                    <Line type="monotone" dataKey="last_year" name="Last Year" stroke="#94a3b8" strokeDasharray="4 4" strokeWidth={2} dot={false} connectNulls />
                    <Line type="monotone" dataKey="forecast" name="Forecast" stroke="#059669" strokeDasharray="2 2" strokeWidth={2} dot={{ r: 2 }} connectNulls />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
            <div style={{ flex: '1 1 220px', minWidth: 220, display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div style={kpiBox}>
                <div style={{ fontSize: 11, color: 'var(--theme-text-muted, #64748b)' }}>Pickup Summary (vs LY)</div>
                <div style={{ fontSize: 22, fontWeight: 800, color: deltaColor(curve.summary.pickup_pct_vs_ly) }}>{fmtPct(curve.summary.pickup_pct_vs_ly)}</div>
                <div style={{ fontSize: 11, color: 'var(--theme-text-muted, #64748b)' }}>{curve.summary.pickup_pct_vs_ly == null ? 'No prior-year data' : (curve.summary.pickup_pct_vs_ly >= 0 ? 'More rooms booked' : 'Fewer rooms booked')}</div>
              </div>
              <div style={kpiBox}>
                <div style={{ fontSize: 11, color: 'var(--theme-text-muted, #64748b)' }}>Rooms on Books</div>
                <div style={{ fontSize: 22, fontWeight: 800 }}>{fmtInt(curve.summary.rooms_on_books)}</div>
                <div style={{ fontSize: 11, color: 'var(--theme-text-muted, #64748b)' }}>vs LY {fmtInt(curve.summary.rooms_on_books_ly)}</div>
              </div>
              <div style={kpiBox}>
                <div style={{ fontSize: 11, color: 'var(--theme-text-muted, #64748b)' }}>Forecast Occupancy</div>
                <div style={{ fontSize: 22, fontWeight: 800, color: '#059669' }}>{curve.forecast_kpis.forecast_occupancy_pct != null ? `${curve.forecast_kpis.forecast_occupancy_pct}%` : '—'}</div>
                <div style={{ fontSize: 11, color: 'var(--theme-text-muted, #64748b)' }}>vs LY actual {curve.forecast_kpis.forecast_occupancy_pct_ly != null ? `${curve.forecast_kpis.forecast_occupancy_pct_ly}%` : '—'}</div>
              </div>
              <div style={kpiBox}>
                <div style={{ fontSize: 11, color: 'var(--theme-text-muted, #64748b)' }}>Projected RevPAR</div>
                <div style={{ fontSize: 22, fontWeight: 800 }}>{fmtInr(curve.forecast_kpis.projected_revpar)}</div>
                <div style={{ fontSize: 11, color: 'var(--theme-text-muted, #64748b)' }}>vs LY {fmtInr(curve.forecast_kpis.projected_revpar_ly)}</div>
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginTop: 16 }}>
            <div style={{ ...card, flex: '1 1 320px', minWidth: 280 }}>
              <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 8 }}>Pace Insights</div>
              <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, lineHeight: 1.7 }}>
                {curve.insights.map((line, i) => <li key={i}>{line}</li>)}
              </ul>
            </div>
            <div style={{ ...card, flex: '1 1 320px', minWidth: 280 }}>
              <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 8 }}>Pickup by Stay Date</div>
              {byWeek.length === 0 ? <div style={emptyBox}>No bookings yet for this window.</div> : (
                <div style={{ width: '100%', height: 200 }}>
                  <ResponsiveContainer>
                    <BarChart data={byWeek} margin={{ top: 5, right: 10, bottom: 5, left: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--theme-border, #e5e7eb)" />
                      <XAxis dataKey="week_start" tick={{ fontSize: 10 }} />
                      <YAxis tick={{ fontSize: 10 }} width={28} />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="this_year" name="This Year" fill="#2563eb" />
                      <Bar dataKey="last_year" name="Last Year" fill="#94a3b8" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {!loading && tab === 'segment' && (
        <div style={card}>
          <div style={{ fontSize: 12, color: 'var(--theme-text-muted, #64748b)', marginBottom: 10 }}>Bookings by source/segment for {roomName}, this year vs last year.</div>
          {segments.length === 0 ? <div style={emptyBox}>No bookings yet for this window.</div> : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr>{['Segment', 'This Year', 'Last Year', 'vs LY'].map((h) => <th key={h} style={th}>{h}</th>)}</tr></thead>
              <tbody>
                {segments.map((s) => (
                  <tr key={s.segment}>
                    <td style={td}>{s.segment}</td>
                    <td style={{ ...td, textAlign: 'right' }}>{fmtInt(s.this_year)}</td>
                    <td style={{ ...td, textAlign: 'right' }}>{fmtInt(s.last_year)}</td>
                    <td style={{ ...td, textAlign: 'right', color: deltaColor(s.pct_vs_last_year), fontWeight: 600 }}>{fmtPct(s.pct_vs_last_year)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {!loading && tab === 'stayDate' && (
        <div style={card}>
          <div style={{ fontSize: 12, color: 'var(--theme-text-muted, #64748b)', marginBottom: 10 }}>
            Per stay-date pace for {roomName} — this year's bookings so far vs last year's bookings at the equivalent lead time (not last year's final total).
          </div>
          {byStayDate.length === 0 ? <div style={emptyBox}>No dates in this window.</div> : (
            <div style={{ maxHeight: 480, overflowY: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead><tr>{['Date', 'This Year', 'Last Year (same lead time)', 'vs LY'].map((h) => <th key={h} style={th}>{h}</th>)}</tr></thead>
                <tbody>
                  {byStayDate.map((d) => (
                    <tr key={d.stay_date}>
                      <td style={td}>{d.stay_date}</td>
                      <td style={{ ...td, textAlign: 'right' }}>{fmtInt(d.this_year)}</td>
                      <td style={{ ...td, textAlign: 'right' }}>{fmtInt(d.last_year)}</td>
                      <td style={{ ...td, textAlign: 'right', color: deltaColor(d.pct_vs_last_year), fontWeight: 600 }}>{fmtPct(d.pct_vs_last_year)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default PickupPace;
